<template>
  <basic-block :value="value" :mode="mode">
    <template v-slot:main>
      <portal :to="`contentFlashcardQuestion-toolbar-${value.id}`" v-if="mode == 'edit'">
        <block-toolbar-button
          icon="mdi-arrow-expand-right"
          tooltip="Create minor version"
          @click="createMinorVersion"
          v-if="isContentSelected"
          :dark="slideStyleData.dark" />
        <p-toolbar-button
          v-if="isContentSelected && !blocksSelector.overlayActive"
          icon="mdi-image-off-outline"
          tooltip="Change Content"
          @click="changeContent"
          :disabled="changeContentProtection">
          Change Content
        </p-toolbar-button>
        <p-toolbar-button
          v-if="isContentSelected && !blocksSelector.overlayActive"
          icon="mdi-restore"
          tooltip="Reset to default styles"
          @click="resetToDefaultStyles"
          :disabled="changeContentProtection">
          Reset to default styles
        </p-toolbar-button>
        <p-toolbar-menu v-if="isContentSelected" icon="mdi-layers-outline" title="Layers" tooltip="Display layers">
          <v-list dense>
            <v-list-tile @click="displayQuestion()">
              <v-list-tile-action>
                <v-icon>mdi-border-all-variant</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Question</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile @click="displayOverlay('answer')">
              <v-list-tile-action>
                <v-icon>mdi-arrange-send-backward</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Answer</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </p-toolbar-menu>
      </portal>
      <template v-if="mode == 'edit'">
        <div class="lp-block-library-content">
          <v-btn color="primary" @click="addFromContentManager" v-if="!isContentSelected" large outline
            >Add from Content Manager
          </v-btn>
          <template v-else>
            <block-subcanvas :value="value" :blocks="value.data.blocks" :mode="mode" :blockVariables="blockVariables" />
          </template>
        </div>
      </template>
      <template v-else>
        <block-subcanvas :value="value" :blocks="value.data.blocks" mode="view" :blockVariables="blockVariables" />
      </template>
    </template>
  </basic-block>
</template>

<script>
import { PToolbarButton, PToolbarMenu } from "plant-common"
import BasicBlock from "../_components/containers/BasicBlock.vue"
import BlockSubcanvas from "../_components/containers/BlockSubcanvas.vue"
import PServiceContent from "plant-common/src/services/PServiceContent"
import BlockToolbarButton from "../button/BlockToolbarButton.vue"
import _ from "lodash"
import { BlockSelectionState } from "../_models/blockSelectionState"

export default {
  name: "BlockFlashCard",
  inject: ["blockEventBus", "blocksSelector", "slideStyleData"],
  props: ["value", "mode"],
  components: {
    PToolbarButton,
    PToolbarMenu,
    BasicBlock,
    BlockSubcanvas,
    BlockToolbarButton
  },
  data() {
    return {
      blockVariables: false
    }
  },
  computed: {
    isContentSelected() {
      return Object.keys(this.value.data.content_ref).length > 0
    },
    answerOverlay() {
      return this.value.data.overlays.answer
    },
    changeContentProtection() {
      return this.value.protection.data
    }
  },
  created() {
    if (this.isContentSelected) this.addVariables()
    if (this.blockEventBus) this.blockEventBus.$on("submit", this.submitted)

    this.blocksSelector.updateOverlayVariablesOnBlockState(this.value.id, this.blockVariables)
  },
  methods: {
    async addFromContentManager() {
      var newContentRef = await PServiceContent.selectContent({ type: "activityFlashCard" })
      if (!newContentRef) return
      this.value.data.content_ref = newContentRef
      this.addVariables()
    },
    submitted() {
      const overlay = this.value.data.overlays.answer
      const overlayVariables = this.blockVariables

      this.blocksSelector.openNewOverlay(overlay, overlayVariables)
    },
    addVariables() {
      if (this.value.data.content_ref.body) {
        this.blockVariables = [
          {
            name: "FlashcardQuestion",
            type: "string",
            value: this.value.data.content_ref.body.contents.flashcard[0].question,
            title: "Question"
          },
          {
            name: "FlashcardAnswer",
            type: "string",
            value: this.value.data.content_ref.body.contents.flashcard[0].answer,
            title: "Answer"
          }
        ]
      }
    },
    changeContent() {
      this.value.data.content_ref = {}
    },
    resetToDefaultStyles() {
      this.value.data = defaultQuestionBlocks()
    },
    displayQuestion() {
      this.closeLayerOverlay()
      this.blocksSelector.selectionClear()
    },
    displayOverlay(type) {
      const overlay = this.value.data.overlays[type]
      overlay.parent = this.value.id

      const overlayVariables = this.blockVariables

      this.blocksSelector.openNewOverlay(overlay, overlayVariables)
    },
    createMinorVersion() {
      if (this.value && this.value.data.content_ref) {
        this.$emit("create:draft", this.value.data.content_ref)
      }
    },
    closeLayerOverlay() {
      const answerOverlayId = this.answerOverlay.id
      const overlayActive = this.blocksSelector.overlayActive

      if (!overlayActive) return
      const overlayActiveId = overlayActive.id

      if (answerOverlayId === overlayActiveId) {
        this.blocksSelector.closeOverlay()
      }
    }
  },
  beforeDestroy() {
    if (this.answerOverlay && !_.isEmpty(this.blocksSelector)) {
      this.blocksSelector.$off(`state-changed:${this.answerOverlay.id}`)
      this.answerOverlay.blocks.forEach(answerBlock => {
        this.blocksSelector.$off(`state-changed:${answerBlock.id}`)
      })
    }
    if (this.blockEventBus) this.blockEventBus.$off("submit", this.submitted)
  },
  watch: {
    "answerOverlay.blocks.length": {
      immediate: true,
      handler() {
        if (this.answerOverlay && !_.isEmpty(this.blocksSelector)) {
          this.blocksSelector.$on(`state-changed:${this.answerOverlay.id}`, state => {
            if (state == BlockSelectionState.primarySelected) {
              this.displayOverlay("answer")
            }
          })
          // if answer overlay have blocks, listen to each block state-change
          this.answerOverlay.blocks.forEach(answerBlock => {
            this.blocksSelector.$on(`state-changed:${answerBlock.id}`, state => {
              if (state == BlockSelectionState.primarySelected) {
                this.displayOverlay("answer")
              }
            })
          })
        }
      }
    },
    "value.data.content_ref": {
      immediate: true,
      deep: true,
      handler() {
        this.addVariables()
      }
    }
  }
}
</script>

<style scoped>
.lp-block-library-content {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: none;
  position: relative;
}
</style>
